import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ILoginResponse, IUser } from "@/types/auth";

interface AuthState {
  user: IUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: () => boolean;
  setAuthData: (data: ILoginResponse) => void;
  setUser: (user: IUser) => void;
  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  clearAuth: () => void;
}

const STORAGE_KEY = "auth-storage";
const PERSISTED_KEYS = ["user", "accessToken", "refreshToken"];

function clearStorageIfShapeMismatched(): void {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return;

  try {
    const keys = Object.keys(JSON.parse(raw)?.state ?? {});
    const matches =
      keys.length === PERSISTED_KEYS.length &&
      PERSISTED_KEYS.every((key) => keys.includes(key));
    if (!matches) localStorage.removeItem(STORAGE_KEY);
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
}

clearStorageIfShapeMismatched();

export const authStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,

      isAuthenticated: () => !!get().accessToken,

      setAuthData: (data) => {
        set({
          user: data.user,
          accessToken: data.access,
          refreshToken: data.refresh,
        });
      },

      setUser: (user) => set({ user }),
      setAccessToken: (token) => set({ accessToken: token }),
      setRefreshToken: (token) => set({ refreshToken: token }),

      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);

// 다른 탭이 로그인·로그아웃·토큰 회전으로 localStorage 를 바꾸면 이 탭의 메모리 상태도 따라간다.
// 안 그러면 이 탭이 오래된 refresh 토큰을 들고 있다가 401 → 로그아웃된다.
window.addEventListener("storage", (event) => {
  if (event.key === STORAGE_KEY) void authStore.persist.rehydrate();
});
